import React, { ComponentProps, ReactNode, useMemo } from 'react';
import { css, cx } from '@linaria/atomic';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Truncated } from '../../../truncated';

const callBubbleDivStyle1 = css`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const callBubbleDivStyle2 = css`
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spacing(1)};
  display: inline-flex;
  min-width: 160px;
`;

const clickableStyle = css`
  cursor: pointer;
`;

const callBubbleDivStyle3 = css`
  border-radius: ${theme.spacing(1)};
  padding-top: ${theme.spacing(0.5)};
  padding-right: ${theme.spacing(0.5)};
  padding-bottom: ${theme.spacing(0.5)};
  padding-left: ${theme.spacing(0.5)};
`;

const inboundIconBgStyle = css`
  background-color: ${theme.colors.primary5};
`;

const outboundIconBgStyle = css`
  background-color: ${theme.colors.neutral5};
`;

const abandonedIconBgStyle = css`
  background-color: #fff6d6;
`;

const voicemailIconBgStyle = css`
  background-color: rgba(0, 0, 0, 0.1);
`;

const defaultIconBgStyle = css`
  background-color: ${theme.colors.critical5};
`;

const callBubbleDivStyle4 = css`
  display: flex;
  gap: ${theme.spacing(0.5)};
  align-items: center;
`;

const callBubbleIconStyle5 = css`
  color: ${theme.colors.neutral70};
`;

const callBubbleIconStyle6 = css`
  color: ${theme.colors.neutral70};
`;

const callBubbleDivStyle7 = css`
  padding-top: ${theme.spacing(0.5)};
  padding-right: ${theme.spacing(0.5)};
  padding-bottom: ${theme.spacing(0.5)};
  padding-left: ${theme.spacing(0.5)};
  border-radius: ${theme.spacing(0.5)};
  margin-left: auto;
  &:hover {
    background-color: ${theme.colors.neutral20};
  }
`;

const callBubbleIconStyle8 = css`
  cursor: pointer;
`;

type CallBubbleProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  callResult?: 'missed' | 'inbound' | 'outbound' | 'abandoned' | 'voicemail';
  callAction?: () => void;
  playAction?: () => void;
  children?: ReactNode;
};

export const CallBubble: React.FC<CallBubbleProps> = ({
  title,
  subtitle,
  callResult,
  callAction,
  playAction,
  children,
}) => {
  const iconName: ComponentProps<typeof Icon>['name'] = useMemo(() => {
    switch (callResult) {
      case 'inbound':
        return 'phone-incoming';
      case 'outbound':
        return 'phone-outgoing-small';
      case 'voicemail':
        return 'voicemail';
      case 'abandoned':
      case 'missed':
        return 'phone-missed';
      default:
        return 'phone';
    }
  }, [callResult]);

  const iconBgClass = useMemo(() => {
    switch (callResult) {
      case 'inbound':
        return inboundIconBgStyle;
      case 'outbound':
        return outboundIconBgStyle;
      case 'abandoned':
        return abandonedIconBgStyle;
      case 'voicemail':
        return voicemailIconBgStyle;
      default:
        return defaultIconBgStyle;
    }
  }, [callResult]);

  return (
    <div className={callBubbleDivStyle1}>
      <div
        onClick={callAction ? callAction : undefined}
        className={cx(callBubbleDivStyle2, callAction && clickableStyle)}
      >
        <div className={cx(callBubbleDivStyle3, iconBgClass)}>
          <Icon name={iconName} size={24} />
        </div>
        <div>
          <Truncated
            style={{
              maxWidth: 120,
              fontSize: theme.fontSize(14),
            }}
          >
            {title}
          </Truncated>
          {subtitle && (
            <div className={callBubbleDivStyle4}>
              {callResult === 'inbound' && <Icon name='phone-call' className={callBubbleIconStyle5} size={12} />}
              {/* Create a new enum for auto message icon once we support other types of robocall */}
              {callResult === 'outbound' && subtitle === 'Appointment Reminder' && (
                <Icon name='automated-message' className={callBubbleIconStyle6} size={12} />
              )}
              <Truncated
                style={{
                  maxWidth: 130,
                  fontSize: theme.fontSize(12),
                  color: theme.font.colors.subdued,
                }}
              >
                {subtitle}
              </Truncated>
            </div>
          )}
        </div>
        {callAction && (
          <div className={callBubbleDivStyle7}>
            <Icon
              name='play'
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                (playAction ?? callAction)?.();
              }}
              className={callBubbleIconStyle8}
              size={16}
            />
          </div>
        )}
      </div>
      {children}
    </div>
  );
};
